import { Link, useParams } from '@tanstack/react-router';
import { ArrowLeft, CalendarClock, History, Pencil, Truck } from 'lucide-react';
import { cn } from '@/lib/cn';
import { formatKg, formatPlate, formatWhen } from '@/lib/format';
import { useSession } from '@/api/session';
import { useVehicle } from '@/api/hooks/catalog';
import { useTripSheet, useVehicleOverview } from '@/api/hooks/trips';
import type { VehicleTripRow } from '@/api/types';
import { Badge, StatusPill } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { Card, CardBody, CardHeader, Stat } from '@/components/ui/Card';
import { EmptyState, ErrorState, LoadingBlock } from '@/components/ui/State';
import { TripSheetView } from '@/components/trips/TripSheetView';
import { VehicleDocumentsCard } from '@/components/vehicles/VehicleDocumentsCard';
import { VanVignettes } from '@/components/vehicles/VanVignettes';
import { VanDriverCard } from '@/components/vehicles/VanDriverCard';
import { VanSignalsCard } from '@/components/vehicles/VanSignalsCard';

/**
 * ─────────────────────────────────────────────────────────────────────────
 *  БУСЪТ — „цъкаш върху дадения бус и се показва всичко".
 *
 *  Подредбата е по това колко често се гледа: сигналите за буса (ако има),
 *  текущият курс на един лист — заявките с трите документа и действията по
 *  тях, гориво, заплата — после шофьорът, данните и талонът на буса, и най-
 *  долу предстоящите и последните курсове. Затова много страници от менюто
 *  са в „Още": каквото трябва за бус, е тук.
 * ─────────────────────────────────────────────────────────────────────────
 */
export function VanPage() {
  const { vehicleId } = useParams({ from: '/vehicles/$vehicleId' });
  const vehicle = useVehicle(vehicleId);
  const overview = useVehicleOverview(vehicleId);
  /** Листът: текущият курс, иначе най-близкият предстоящ. */
  const focusId = overview.data?.current?.id ?? overview.data?.upcoming[0]?.id;
  const sheet = useTripSheet(focusId);
  const role = useSession((st) => st.user?.role);

  if (vehicle.isLoading || overview.isLoading) return <LoadingBlock />;
  if (vehicle.isError || !vehicle.data) return <ErrorState onRetry={() => void vehicle.refetch()} />;
  const v = vehicle.data;
  const rest = (overview.data?.upcoming ?? []).filter((t) => t.id !== focusId);
  /** Шофьорът на курса и този, който е зачислен към буса — обикновено е един и същ. */
  const tripDriverId = sheet.data?.trip.driverId ?? null;
  const driverIds = [
    ...(tripDriverId ? [{ id: tripDriverId, role: `в курса ${sheet.data?.trip.number ?? ''}`.trim() }] : []),
    ...(v.currentDriver?.id && v.currentDriver.id !== tripDriverId ? [{ id: v.currentDriver.id, role: 'зачислен към буса' }] : []),
  ];

  return (
    <div className="flex min-h-full flex-col gap-3 p-3 sm:gap-4 sm:p-4">
      <div className="flex flex-wrap items-center gap-3">
        <Link to="/" className="text-ink-dim hover:text-accent" title="Към началото">
          <ArrowLeft className="size-5" />
        </Link>
        <div className="flex size-11 items-center justify-center rounded-lg bg-raised font-display text-lg font-bold">
          {v.fleetNo}
        </div>
        <div>
          <div className="font-mono text-base tracking-wide">{formatPlate(v.plate)}</div>
          <div className="text-xs text-ink-dim">
            {[v.make, v.model, v.year].filter(Boolean).join(' ') || '—'}
            {v.currentDriver ? ` · ${v.currentDriver.name}` : ''}
          </div>
        </div>
        <Badge tone={v.status === 'active' ? 'ok' : 'warn'} className="ml-1">
          {v.operationalStatusLabel ?? v.status}
        </Badge>
        <Link to="/vehicles" search={{ edit: v.id }} className="ml-auto">
          <Button size="sm" variant="secondary" icon={<Pencil className="size-3.5" />}>
            Данни на буса
          </Button>
        </Link>
      </div>

      <VanSignalsCard vehicleId={v.id} />

      {focusId ? (
        sheet.data ? (
          <TripSheetView sheet={sheet.data} compact docActions canIssue={role === 'owner' || role === 'accountant'} />
        ) : sheet.isLoading ? (
          <LoadingBlock />
        ) : null
      ) : (
        <Card>
          <EmptyState title="Бусът няма текущ или планиран курс." body="Назначете заявка от Началото или от самата заявка." />
        </Card>
      )}

      {driverIds.length ? (
        <div className={cn('grid gap-3 sm:gap-4', driverIds.length > 1 && 'lg:grid-cols-2')}>
          {driverIds.map((d) => (
            <VanDriverCard key={d.id} driverId={d.id} role={d.role} />
          ))}
        </div>
      ) : null}

      <Card>
        <CardBody className="grid grid-cols-2 gap-4 sm:grid-cols-4">
          <Stat
            label="Товароносимост"
            value={formatKg(v.payloadKg)}
            hint={
              v.payloadSource === 'registration'
                ? `F.2 ${formatKg(v.permissibleMassKg)} − G ${formatKg(v.kerbMassKg ?? null)}`
                : v.payloadSource === 'manual'
                  ? 'ръчно въведена — попълнете G от талона'
                  : 'въведете F.2 и G от талона'
            }
            tone={v.payloadKg === null ? 'warn' : 'default'}
          />
          <Stat
            label="Товарен обем"
            value={v.cargoVolumeM3 != null ? `${v.cargoVolumeM3.toLocaleString('bg-BG', { maximumFractionDigits: 1 })} m³` : '—'}
            tone={v.cargoVolumeM3 == null ? 'warn' : 'default'}
          />
          <Stat label="Допустима маса (F.2)" value={formatKg(v.permissibleMassKg)} />
          <Stat
            label="Километраж"
            value={v.odometerKm != null ? `${v.odometerKm.toLocaleString('bg-BG')} км` : '—'}
            hint={v.odometerAt ? formatWhen(v.odometerAt) : undefined}
          />
          <VanVignettes vehicleId={v.id} className="col-span-2 border-t border-line pt-3 sm:col-span-4" />
        </CardBody>
      </Card>

      <VehicleDocumentsCard vehicleId={v.id} missingRegistrationData={v.payloadSource !== 'registration'} />

      <div className="grid gap-3 sm:gap-4 lg:grid-cols-2">
        <TripRows
          title="Предстоящи курсове"
          icon={<CalendarClock className="size-4 text-ink-dim" />}
          rows={rest}
          empty="Няма други планирани курсове."
        />
        <TripRows
          title="Последни курсове"
          icon={<History className="size-4 text-ink-dim" />}
          rows={overview.data?.recent ?? []}
          empty="Още няма приключени курсове."
        />
      </div>
    </div>
  );
}

function TripRows({
  title,
  icon,
  rows,
  empty,
}: {
  title: string;
  icon: React.ReactNode;
  rows: VehicleTripRow[];
  empty: string;
}) {
  return (
    <Card className="overflow-hidden">
      <CardHeader
        title={
          <span className="flex items-center gap-1.5">
            {icon}
            {title}
          </span>
        }
      />
      {rows.length === 0 ? (
        <CardBody>
          <p className="text-xs text-ink-faint">{empty}</p>
        </CardBody>
      ) : (
        <ul className="divide-y divide-line">
          {rows.map((r) => (
            <li key={r.id}>
              <Link
                to="/trips/$tripId"
                params={{ tripId: r.id }}
                className="flex flex-wrap items-center gap-x-3 gap-y-1 px-4 py-2.5 text-sm hover:bg-raised"
              >
                <Truck className="size-4 text-ink-faint" />
                <span className="font-mono text-accent">{r.number}</span>
                <StatusPill status={r.status} kind="trip" />
                <span className="text-xs text-ink-dim">
                  {r.from ?? '—'} → {r.to ?? '—'}
                </span>
                <span className="ml-auto flex items-center gap-2 text-xs text-ink-dim">
                  <span>
                    {r.orders} {r.orders === 1 ? 'заявка' : 'заявки'}
                  </span>
                  <span className={cn(r.docsComplete < r.orders ? 'text-warn' : 'text-ok')}>
                    документи {r.docsComplete}/{r.orders}
                  </span>
                  {r.weightPct !== null ? (
                    <span className={cn(r.overweight ? 'text-danger' : undefined)}>{r.weightPct} % тегло</span>
                  ) : null}
                  <span>{formatWhen(r.completedAt ?? r.startedAt ?? r.plannedStartAt)}</span>
                </span>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}
